import type { AutomationView, TileColumns, UiTheme } from '@adweb/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../lib/api-client';
import { DisplayOptions } from '../preferences/DisplayOptions';
import { UI_PREFERENCES_KEY } from '../preferences/ui-preferences';

interface OrgSettings {
  uiTheme?: UiTheme;
  automationView?: AutomationView;
  tileColumns?: TileColumns;
}

/**
 * Affichage par défaut d'une organisation (rôles Adweb-Systèmes). S'applique à
 * tous ses utilisateurs qui n'ont pas fait leur propre choix.
 */
export function OrganizationDisplayModal({
  org,
  onClose,
}: {
  org: { id: string; name: string } | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [uiTheme, setUiTheme] = useState<UiTheme>('MARINE');
  const [automationView, setAutomationView] = useState<AutomationView>('LIST');
  const [tileColumns, setTileColumns] = useState<TileColumns>(3);

  const settings = useQuery({
    queryKey: ['organizations', org?.id, 'settings'],
    queryFn: async () =>
      (await apiClient.get<OrgSettings>(`/organizations/${org?.id}/settings`)).data,
    enabled: !!org,
  });

  useEffect(() => {
    if (!settings.data) return;
    setUiTheme(settings.data.uiTheme ?? 'MARINE');
    setAutomationView(settings.data.automationView ?? 'LIST');
    setTileColumns(settings.data.tileColumns ?? 3);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      apiClient.patch(`/organizations/${org?.id}/settings`, {
        uiTheme,
        automationView,
        tileColumns,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['organizations', org?.id, 'settings'] });
      await qc.invalidateQueries({ queryKey: UI_PREFERENCES_KEY });
      onClose();
    },
  });

  return (
    <Modal
      open={!!org}
      onClose={onClose}
      title={`Affichage par défaut — ${org?.name ?? ''}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || settings.isLoading}>
            {save.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <p className="mb-4 text-sm text-slate-600">
        S’applique à tous les utilisateurs de l’organisation qui n’ont pas choisi leur propre
        affichage. Chacun peut ensuite le modifier depuis son menu.
      </p>
      <DisplayOptions
        idPrefix={`org-${org?.id ?? 'none'}`}
        uiTheme={uiTheme}
        automationView={automationView}
        tileColumns={tileColumns}
        onThemeChange={setUiTheme}
        onViewChange={setAutomationView}
        onTileColumnsChange={setTileColumns}
        disabled={settings.isLoading}
      />
      {save.isError && (
        <p role="alert" className="mt-3 text-xs text-red-600">
          L’enregistrement a échoué. Réessayez dans un instant.
        </p>
      )}
    </Modal>
  );
}
